import React, { useState, useEffect, useRef } from "react";
import styles from "./PreviewProductDescription.module.css";
import { BiLaptop, BiMobile } from "react-icons/bi";
import { LiaCommentDots } from "react-icons/lia";
import CommentBox from "../../components/Common/CommentBox";
import { commentService } from "../../services/commentService";
import { useParams } from "react-router-dom";
import { useAuth } from "../../components/Context/authContext";
import { toast } from "react-toastify";

const PreviewProductDescription = ({ data, setPreviewMode, previewMode }) => {
    const { documentId } = useParams();
    const { user: currentUser } = useAuth();
    
    // Comment system state
    const previewRef = useRef(null);
    const [mouseY, setMouseY] = useState(window.innerHeight / 2);
    const [insidePreview, setInsidePreview] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [comments, setComments] = useState([]);
    const [showCommentBox, setShowCommentBox] = useState(false);
    const [commentPosition, setCommentPosition] = useState(null);

    // Load comments from backend when component mounts
    useEffect(() => {
        if (documentId) {
            loadComments();
        }
    }, [documentId]);

    const loadComments = async () => {
        try {
            const response = await commentService.getComments('Product', documentId);
            const formattedComments = response.map(comment => ({
                id: comment.id,
                text: comment.content,
                position: comment.position_y,
                isSaved: true,
                user: comment.user,
                createdAt: comment.formatted_created_at
            }));
            setComments(formattedComments);
        } catch (error) {
            console.error('Failed to load comments:', error);
            // Don't show error toast for comments as it's not critical
        }
    };

    const handleCommentClick = () => {
        // Allow anonymous users to open the comment box
        setCommentPosition(mouseY);
        setShowCommentBox(true);
    };

    const handleSaveComment = async (text) => {
        if (!text.trim() || !documentId) return;
        try {
            const commentData = {
                content: text.trim(),
                position_x: 0, // Default x position
                position_y: commentPosition
            };
            
            const response = await commentService.createComment('Product', documentId, commentData);
            const newComment = {
                id: response.id,
                text: response.content,
                position: response.position_y,
                isSaved: true,
                user: response.user, // may be null for anonymous
                createdAt: response.formatted_created_at
            };
            
            setComments([...comments, newComment]);
            setShowCommentBox(false);
        } catch (error) {
            console.error('Failed to save comment:', error);
            toast.error('Failed to save comment. Please try again.');
        }
    };

    const handleEditComment = async (index, newText) => {
        const comment = comments[index];
        if (!comment.id) {
            // Handle local comments that haven't been saved yet
            const updatedComments = [...comments];
            updatedComments[index].text = newText;
            setComments(updatedComments);
            return;
        }
        
        if (documentId) {
            try {
                const commentData = {
                    content: newText.trim()
                };
                
                const response = await commentService.updateComment('Product', documentId, comment.id, commentData);
                const updatedComments = [...comments];
                updatedComments[index] = {
                    ...updatedComments[index],
                    text: response.content,
                    createdAt: response.formatted_created_at
                };
                setComments(updatedComments);
            } catch (error) {
                console.error('Failed to update comment:', error);
                // Fallback to local update if API fails
                const updatedComments = [...comments];
                updatedComments[index].text = newText;
                setComments(updatedComments);
            }
        }
    };

    const handleDeleteComment = async (index) => {
        const comment = comments[index];
        if (!comment.id) {
            // Handle local comments that haven't been saved yet
            setComments(comments.filter((_, i) => i !== index));
            return;
        }
        
        if (documentId) {
            try {
                await commentService.deleteComment('Product', documentId, comment.id);
                setComments(comments.filter((_, i) => i !== index));
            } catch (error) {
                console.error('Failed to delete comment:', error);
            }
        }
    };

    // Track mouse position within preview area for comment placement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (previewRef.current && previewRef.current.contains(e.target)) {
                const rect = previewRef.current.getBoundingClientRect();
                setMouseY(e.clientY - rect.top);
                setInsidePreview(true);
            } else {
                setInsidePreview(false);
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    return (
        <div className={styles.previewContainer}>
            {/* Device Toggle */}
            <div className={styles.deviceToggle} role="group" aria-label="Toggle preview device size">
                <button
                    aria-pressed={previewMode === "desktop"}
                    onClick={() => setPreviewMode("desktop")}
                    className={`${styles.deviceButton} ${previewMode === "desktop" ? styles.active : ""}`}
                >
                    <BiLaptop aria-hidden />
                    <span className="visually-hidden">Desktop view</span>
                </button>
                <button
                    aria-pressed={previewMode === "mobile"}
                    onClick={() => setPreviewMode("mobile")}
                    className={`${styles.deviceButton} ${previewMode === "mobile" ? styles.active : ""}`}
                >
                    <BiMobile aria-hidden />
                    <span className="visually-hidden">Mobile view</span>
                </button>
            </div>

            <div className={styles.previewWrapper}>
                <div
                    ref={previewRef}
                    className={`${styles.previewContent} ${previewMode === "mobile" ? styles.mobile : styles.desktop}`}
                >
                    {/* Floating Comment Button - only show for authenticated users */}
                    {!showCommentBox && (
                        <div
                            style={{
                                position: "absolute",
                                right: "-60px",
                                top: `${mouseY}px`,
                                zIndex: 1000,
                                cursor: "pointer",
                                fontSize: "24px",
                                color: isHovered ? "#D04734" : "#666",
                                backgroundColor: isHovered ? "rgba(208, 71, 52, 0.1)" : "rgba(0, 0, 0, 0.1)",
                                borderRadius: "50%",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: isHovered ? "0 4px 12px rgba(208, 71, 52, 0.3)" : "0 2px 8px rgba(0,0,0,0.15)",
                                transition: "all 0.2s ease",
                                animation: isHovered ? "heartbeat 1.5s ease-in-out infinite" : "none",
                                border: isHovered ? "2px solid #D04734" : "1px solid rgba(0,0,0,0.1)",
                            }}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCommentClick();
                            }}
                            aria-label="Add comment"
                            title={"Add comment"}
                        >
                            <LiaCommentDots />
                        </div>
                    )}

                    {/* Existing Comments */}
                    {comments.map((comment, index) => (
                        <CommentBox
                            key={comment.id || `temp-${index}`}
                            position={comment.position}
                            commentText={comment.text}
                            isSaved={comment.isSaved}
                            onSave={(text) => handleEditComment(index, text)}
                            onEdit={(newText) => handleEditComment(index, newText)}
                            onDelete={() => handleDeleteComment(index)}
                            user={comment.user}
                            canModify={!!currentUser}
                        />
                    ))}

                    {/* New Comment Box - allow for anonymous too */}
                    {showCommentBox && (
                        <CommentBox
                            position={commentPosition}
                            onSave={handleSaveComment}
                            onCancel={() => setShowCommentBox(false)}
                            user={currentUser}
                        />
                    )}

                    {/* Render product description content */}
                    {/* ... existing product rendering code ... */}
                </div>
            </div>
        </div>
    );
};

export default PreviewProductDescription;
